"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import Image, { StaticImageData } from "next/image";
import { useState, useEffect } from "react";
import { useSwipeable } from "react-swipeable";
import { variants } from "@/utils/animationVariants";
import Link from "next/link";
import { Route } from "@/routers/types";

export interface GallerySliderProps {
  className?: string;
  style?: React.CSSProperties;
  galleryImgs: (StaticImageData | string)[];
  ratioClass?: string;
  uniqueID: string;
  href?: Route<string> | any;
  imageClass?: string;
  galleryClass?: string;
  navigation?: boolean;
  unoptimized?: boolean;
  /** Marks this gallery's first image as the page's LCP candidate. Only
   *  one slider per page should get this — preloading multiple images
   *  competes for bandwidth and pushes the real LCP back. */
  isLcpCandidate?: boolean;
}

export default function GallerySlider({
  className = "",
  style,
  galleryImgs,
  ratioClass = "aspect-w-4 aspect-h-3",
  imageClass = "",
  uniqueID = "uniqueID",
  galleryClass = "",//"rounded-xl",
  href = "#",
  navigation = true,
  unoptimized = false,
  isLcpCandidate = false,
}: GallerySliderProps) {
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const images = galleryImgs;
  const imageSrc = "/images/default.png";


  function changePhotoId(newVal: number) {
    if (newVal > index) {
      setDirection(1);
    } else {
      setDirection(-1);
    }
    setIndex(newVal);
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (index < images?.length - 1) {
        changePhotoId(index + 1);
      }
    },
    onSwipedRight: () => {
      if (index > 0) {
        changePhotoId(index - 1);
      }
    },
    trackMouse: true,
  });

  let currentImage = images[index] || imageSrc;

  return (
    <MotionConfig
      transition={{
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 },
      }}
    >
      <div
        style={style}
        className={`relative group group/cardGallerySlider ${className}`}
        {...handlers}
      >
        {/* Main image */}
        <div style={style} className={`w-full overflow-hidden ${galleryClass}`}>
          <Link
            href={href}
            className={`relative flex items-center justify-center ${ratioClass}`}
          >
            <AnimatePresence initial={true} custom={direction}>
              <motion.div
                key={index}
                custom={direction}
                variants={variants(340, 1)}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute inset-0"
              >
                <div
                  className={`relative w-full h-full ${!style?.height ? 'aspect-w-4 aspect-h-3 min-h-[180px]' : ''}`}
                  style={style}
                >
                  {!loaded && (
                    <div className="absolute inset-0 shimmer-wrapper">
                      <div className="shimmer" />
                    </div>
                  )}
                  <Image
                    // Skip Next.js image optimization in dev (Sharp is slow on
                    // localhost), but rely on it in prod so CloudFront images
                    // get resized to display dimensions, served as WebP/AVIF,
                    // and cached with `Cache-Control: public, immutable`.
                    unoptimized={process.env.NODE_ENV === "development"}
                    src={currentImage || ""}
                    fill
                    alt="listing card gallery"
                    className={`object-cover ${imageClass}`}
                    onLoad={() => setLoaded(true)}
                    sizes="(max-width: 640px) 50vw, 300px"
                    // Card thumbnails — 60% quality is visually
                    // indistinguishable at thumbnail size and shaves ~40% of
                    // bytes off the tour-grid LCP path.
                    quality={60}
                    // Preload only the LCP candidate (the very first card's
                    // first image). Other gallery firsts are still eager but
                    // not preloaded — keeps the LCP image at the top of the
                    // network priority queue.
                    priority={isLcpCandidate && index === 0}
                    loading={index === 0 ? "eager" : "lazy"}
                    placeholder="empty"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </Link>
        </div>

        {/* Buttons + bottom nav bar */}
        <>
          {/* Buttons */}
          {loaded && navigation && (
            <div className="opacity-0 group-hover/cardGallerySlider:opacity-100 transition-opacity ">
              {index > 0 && (
                <button
                  type="button"
                  aria-label="Previous photo"
                  className="absolute w-8 h-8 left-3 top-[calc(50%-16px)] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 dark:hover:border-neutral-500 rounded-full flex items-center justify-center hover:border-neutral-300 focus:outline-none"
                  style={{ transform: "translate3d(0, 0, 0)" }}
                  onClick={() => changePhotoId(index - 1)}
                >
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              {index + 1 < images.length && (
                <button
                  type="button"
                  aria-label="Next photo"
                  className="absolute w-8 h-8 right-3 top-[calc(50%-16px)] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 dark:hover:border-neutral-500 rounded-full flex items-center justify-center hover:border-neutral-300 focus:outline-none"
                  style={{ transform: "translate3d(0, 0, 0)" }}
                  onClick={() => changePhotoId(index + 1)}
                >
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {/* Bottom Nav bar */}
          <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-neutral-900 opacity-50"></div>
          <div className="flex items-center justify-center absolute bottom-2 left-1/2 transform -translate-x-1/2 space-x-1.5">
            {images.map((_, i) => (
              <button
                type="button"
                aria-label={`Go to photo ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                className={`w-1.5 h-1.5 rounded-full ${
                  i === index ? "bg-white" : "bg-white/60 "
                }`}
                onClick={() => changePhotoId(i)}
                key={i}
              />
            ))}
          </div>
        </>
      </div>
    </MotionConfig>
  );
}
